import { Injectable, signal } from '@angular/core';
import {
  EVENTQ_TOAST_DEFAULT_DURATION_MS,
  EVENTQ_TOAST_EXIT_ANIMATION_MS,
  EVENTQ_TOAST_PRESETS,
  EventqToastMessage,
  EventqToastPosition,
  EventqToastShowOptions,
  EventqToastVariantId,
} from './toast.types';
import { prefixUrl } from '../../utils/url.util';

@Injectable({ providedIn: 'root' })
export class EventqToastService {
  private nextId = 0;
  private readonly dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly exitTimers = new Map<string, ReturnType<typeof setTimeout>>();

  readonly position = signal<EventqToastPosition>('top-right');
  readonly toasts = signal<EventqToastMessage[]>([]);

  show(variantId: EventqToastVariantId, options: EventqToastShowOptions = {}): void {
    const preset = EVENTQ_TOAST_PRESETS[variantId];
    const id = `toast-${++this.nextId}`;
    const durationMs = options.durationMs ?? EVENTQ_TOAST_DEFAULT_DURATION_MS;
    const message: EventqToastMessage = {
      id,
      variantId,
      title: options.title ?? preset.title,
      subtitle: options.subtitle ?? '',
      iconSrc: prefixUrl(
        options.iconSrc ??
          (options.icon ? EVENTQ_TOAST_PRESETS[options.icon].iconSrc : preset.iconSrc),
      ),
      durationMs,
      tone: options.tone,
      exiting: false,
    };

    this.toasts.update((items) => [...items, message]);

    if (durationMs > 0) {
      const timer = setTimeout(() => this.dismiss(id), durationMs);
      this.dismissTimers.set(id, timer);
    }
  }

  snapshotCaptured(cameraName: string): void {
    this.show('snapshot-captured', {
      subtitle: `Snapshot captured from ${cameraName}`,
    });
  }

  snapshotFailed(): void {
    this.show('snapshot-failed', {
      subtitle: 'Could not capture a snapshot from the video. Try again.',
    });
  }

  raisedToResolve(cameraName: string, outcomeLabel: string): void {
    this.show('raised-to-resolve', {
      subtitle: `${cameraName} reported - ${outcomeLabel}.`,
    });
  }

  aiFeedbackReceived(): void {
    this.show('ai-feedback-received', {
      subtitle: 'Your feedback helps make detections smarter.',
    });
  }

  aiFeedbackFailed(): void {
    this.show('ai-feedback-failed', {
      subtitle: 'Could not submit AI feedback. Try again.',
    });
  }

  eventClosed(siteName: string): void {
    this.show('event-closed', {
      subtitle: `${siteName} Event is closed.`,
    });
  }

  eventParked(siteName: string, durationLabel: string): void {
    this.show('event-parked', {
      subtitle: `${siteName} Event is parked for ${durationLabel}.`,
    });
  }

  cameraIssueReported(cameraName: string, reasonLabel: string): void {
    this.show('camera-issue-reported', {
      subtitle: `${cameraName} reported - ${reasonLabel}.`,
    });
  }

  autoProcessOn(): void {
    this.show('auto-process-on', {
      subtitle: 'An event will load immediately after processing an event.',
    });
  }

  autoProcessOff(): void {
    this.show('auto-process-off', {
      subtitle: 'Events will need to be selected or processed in the waiting events list.',
    });
  }

  siteArmed(siteName: string): void {
    this.show('site-armed', {
      subtitle: `${siteName} is now armed.`,
    });
  }

  siteDisarmed(siteName: string): void {
    this.show('site-disarmed', {
      subtitle: `${siteName} is now disarmed.`,
    });
  }

  alarmClipAttached(cameraName: string): void {
    this.show('alarm-clip-attached', {
      subtitle: `Alarm Clip added from ${cameraName}.`,
    });
  }

  alarmClipAttachFailed(subtitle: string = 'Alarm Clip already exists in Resolve.'): void {
    this.show('alarm-clip-attach-failed', { subtitle });
  }

  downloadFailed(): void {
    this.show('download-failed', {
      subtitle: 'File could not be downloaded. Try again.',
    });
  }

  downloadResolved(): void {
    this.show('download-resolved', {
      subtitle: 'File has been downloaded successfully.',
    });
  }

  incidentReportCreated(cameraOrSiteLabel: string, outcomeLabel: string): void {
    this.show('incident-report-created', {
      subtitle: `${cameraOrSiteLabel} reported - ${outcomeLabel}.`,
    });
  }

  evidenceDeleteFailed(): void {
    this.show('evidence-delete-failed', {
      subtitle: 'Could not delete the snapshot or clip. Try again.',
    });
  }

  dismiss(id: string): void {
    const timer = this.dismissTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.dismissTimers.delete(id);
    }

    const target = this.toasts().find((item) => item.id === id);
    if (!target || target.exiting) {
      return;
    }

    this.toasts.update((items) =>
      items.map((item) => (item.id === id ? { ...item, exiting: true } : item)),
    );

    const exitTimer = setTimeout(() => {
      this.exitTimers.delete(id);
      this.toasts.update((items) => items.filter((item) => item.id !== id));
    }, EVENTQ_TOAST_EXIT_ANIMATION_MS);
    this.exitTimers.set(id, exitTimer);
  }

  clear(): void {
    for (const timer of this.dismissTimers.values()) {
      clearTimeout(timer);
    }
    for (const timer of this.exitTimers.values()) {
      clearTimeout(timer);
    }
    this.dismissTimers.clear();
    this.exitTimers.clear();
    this.toasts.set([]);
  }
}
